import { toRaw } from 'vue'
import { useUserStoreWithOut } from '@/store-pinia/modules/user'
import { usePermissionStoreWithOut } from '@/store-pinia/modules/permission'
import { useTagsViewStoreWithOut } from '@/store-pinia/modules/tagsView'
import { useLocaleStoreWithOut } from '@/store-pinia/modules/locale'
import { useBaseStoreWithOut } from '@/store-pinia/modules/base'
import { listenerLocaleChange } from '@/locales/localeChange'
import { useDetailIframe } from '@/layouts/iframe/useDetailIframe'

import { router } from '@/router'
import { useGo, useRedo } from './usePage'
import { useLocale } from './useLocale'

type PostMessageEventData = {
  cmd: string
  data: any
}

let contextBridge: ContextBridge

export function setupExposeInMainWorld() {
  const go = useGo(router)
  const redo = useRedo(router)
  const { detailIframesRef, addDetailIframe } = useDetailIframe()

  contextBridge = {
    getUserStore: useUserStoreWithOut,
    getPermissionStore: usePermissionStoreWithOut,
    getTagsViewStore: useTagsViewStoreWithOut,
    getLocaleStore: useLocaleStoreWithOut,
    getBaseStore: useBaseStoreWithOut,
    go,
    redo,
    back: () => {
      router.back()
    },
    goPage: ({ path, closeCurrent }) => {
      if (closeCurrent) {
        const tabStore = useTagsViewStoreWithOut()
        tabStore.closeTagView(router.currentRoute.value, router)
      }
      go(`/web/main-${path}`)
    },
    backAndClose: () => {
      const tabStore = useTagsViewStoreWithOut()
      tabStore.closeTagView(router.currentRoute.value, router)
      router.back()
    },
    getLocaleMessage: () => {
      const { getLocaleMessage } = useLocale()
      return toRaw(getLocaleMessage.value)
    },
    listenLocaleChange: listenerLocaleChange,
    openDetail: async (path, onlyOne: boolean = false) => {
      if (onlyOne) {
        const pathname = path.split('?')[0]
        const findDetailFrame = detailIframesRef.value.find((d) => d.frameSrc.includes(pathname))
        if (findDetailFrame && !findDetailFrame.frameSrc.endsWith(path)) {
          const tabStore = useTagsViewStoreWithOut()
          tabStore.closeTagViewByKey(findDetailFrame.path, router)
        }
      }
      addDetailIframe(path)
    },
    logout: () => {
      const userStore = useUserStoreWithOut()
      userStore.logout(true)
    },
  }

  window.contextBridge = contextBridge

  const commandHandlers = new Map<string, ContextBridge[keyof ContextBridge]>()

  Object.keys(contextBridge).forEach((key) => commandHandlers.set(key, contextBridge[key]))

  window.addEventListener('message', (event: MessageEvent<PostMessageEventData>) => {
    const handler = commandHandlers.get(event.data.cmd)
    if (typeof handler === 'function') {
      handler(event.data.data)
    }
  })
}

export function exposeInMainWorld<T extends keyof ContextBridge & string>(
  key: T,
  api: ContextBridge[T],
) {
  contextBridge[key] = api
}
